import React, { useMemo } from 'react';
import { hexToRgba, normalizeHex } from '../utils/colorUtils';
import { normalizePaint, paintToCss } from '../utils/paintUtils';
import { parseViewBox } from '../utils/frameUtils';

const buildPathTransform = (frameW, frameH, viewBox) => {
  const meta = parseViewBox(viewBox);
  const safeW = Math.max(1, meta.width || 100);
  const safeH = Math.max(1, meta.height || 100);
  const scaleX = frameW / safeW;
  const scaleY = frameH / safeH;
  const translateX = -meta.minX * scaleX;
  const translateY = -meta.minY * scaleY;
  return {
    meta,
    transform: `translate(${translateX} ${translateY}) scale(${scaleX} ${scaleY})`,
  };
};

const getShadowCss = (shadow) => {
  if (!shadow?.enabled) return 'none';
  const color = normalizeHex(shadow.color || '#000000') || '#000000';
  const opacity = typeof shadow.opacity === 'number' ? shadow.opacity : 0;
  const x = Number(shadow.x) || 0;
  const y = Number(shadow.y) || 0;
  const blur = Number(shadow.blur) || 0;
  return `${x}px ${y}px ${blur}px ${hexToRgba(color, opacity)}`;
};

const FrameElement = ({
  element,
  isSelected,
  isSpacePressed,
  onPointerDown,
  isCropActive,
  onCropPointerDown,
  isDropHover,
  debugMode,
}) => {
  const frame = element.frame || {};
  const border = frame.border || {};
  const shadow = frame.shadow || {};
  const fillEnabled = element.fillEnabled !== false;
  const backgroundPaint = normalizePaint(element.backgroundPaint, '#F6F2E8');
  const backgroundStyle = !fillEnabled
    ? { backgroundColor: 'transparent', backgroundImage: 'none' }
    : backgroundPaint.type === 'gradient'
      ? { backgroundImage: paintToCss(backgroundPaint), backgroundColor: 'transparent' }
      : { backgroundColor: paintToCss(backgroundPaint) };

  const content = element.content && element.content.type === 'image' ? element.content : null;
  const clipId = `frame-clip-${element.id}`;
  const shapeId = `frame-shape-${element.id}`;
  const frameW = Math.max(1, Number(element.w) || 1);
  const frameH = Math.max(1, Number(element.h) || 1);
  const cornerRadius = Number(frame.cornerRadius) || 0;
  const shapeType = frame.shapeType || (frame.pathD ? 'path' : 'rect');
  const pathD = frame.pathD || '';

  const { meta: viewBoxMeta, transform: pathTransform } = useMemo(
    () => buildPathTransform(frameW, frameH, frame.viewBox || '0 0 100 100'),
    [frame.viewBox, frameH, frameW],
  );

  const imageMetrics = useMemo(() => {
    if (!content) return null;
    const naturalW = Math.max(1, Number(content.naturalW) || frameW);
    const naturalH = Math.max(1, Number(content.naturalH) || frameH);
    const scale = Math.max(0.01, Number(content.scale) || 1);
    const width = naturalW * scale;
    const height = naturalH * scale;
    const x = Number(content.offsetX) || 0;
    const y = Number(content.offsetY) || 0;
    return {
      href: content.src,
      x,
      y,
      width,
      height,
    };
  }, [content, frameH, frameW]);

  const borderColor = normalizeHex(border.color || '#FFFFFF') || '#FFFFFF';
  const borderWidth = Math.max(0, Number(border.width) || 0);
  const borderEnabled = Boolean(border.enabled) && borderWidth > 0;
  const borderStroke = hexToRgba(borderColor, 1);
  const shadowCss = getShadowCss(shadow);

  const renderShape = (props = {}) => {
    if (shapeType === 'circle') {
      const radius = Math.min(frameW, frameH) / 2;
      return (
        <circle
          {...props}
          cx={frameW / 2}
          cy={frameH / 2}
          r={radius}
        />
      );
    }
    if (shapeType === 'path' && pathD) {
      return (
        <path
          {...props}
          d={pathD}
          transform={pathTransform}
          vectorEffect="non-scaling-stroke"
        />
      );
    }
    const rx = shapeType === 'rect' ? cornerRadius : 0;
    return (
      <rect
        {...props}
        x={0}
        y={0}
        width={frameW}
        height={frameH}
        rx={rx}
        ry={rx}
      />
    );
  };

  const wrapperClass = [
    'studio-element',
    'studio-frame-element',
    isSelected ? 'is-selected' : '',
    element.locked ? 'is-locked' : '',
    isCropActive ? 'is-cropping' : '',
    isDropHover ? 'is-drop-hover' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={wrapperClass}
      data-element-id={element.id}
      style={{
        left: element.x,
        top: element.y,
        width: frameW,
        height: frameH,
        transform: `rotate(${element.rotation || 0}deg)`,
        opacity: element.opacity ?? 1,
        zIndex: element.zIndex ?? 1,
        boxShadow: shadowCss,
        borderRadius: shapeType === 'rect' ? cornerRadius : shapeType === 'circle' ? '50%' : 0,
      }}
      onPointerDown={(event) => {
        if (isSpacePressed) return;
        event.stopPropagation();
        if ((isCropActive || (event.altKey && content)) && onCropPointerDown) {
          onCropPointerDown(event, element, { autoStart: !isCropActive });
          return;
        }
        onPointerDown(event);
      }}
    >
      <div
        className="studio-frame-surface"
        style={backgroundStyle}
      >
        <svg
          className="studio-frame-svg"
          width="100%"
          height="100%"
          viewBox={`0 0 ${frameW} ${frameH}`}
          preserveAspectRatio="none"
        >
          <defs>
            <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
              {renderShape({ id: shapeId })}
            </clipPath>
          </defs>

          {imageMetrics && (
            <image
              href={imageMetrics.href}
              x={imageMetrics.x}
              y={imageMetrics.y}
              width={imageMetrics.width}
              height={imageMetrics.height}
              preserveAspectRatio="none"
              clipPath={`url(#${clipId})`}
              draggable={false}
            />
          )}

          {!imageMetrics && (
            <g clipPath={`url(#${clipId})`}>
              <rect
                x={0}
                y={0}
                width={frameW}
                height={frameH}
                fill="rgba(255,255,255,0.06)"
              />
            </g>
          )}

          {borderEnabled
            && renderShape({
              fill: 'none',
              stroke: borderStroke,
              strokeWidth: borderWidth,
            })}
        </svg>

        {!imageMetrics && (
          <div className="studio-frame-placeholder">
            Drop image here
          </div>
        )}

        {isCropActive && <div className="studio-frame-crop-overlay" />}
        {debugMode && (
          <div className="studio-frame-debug">
            {content ? 'FILLED' : 'EMPTY'}
          </div>
        )}
      </div>
    </div>
  );
};

export default FrameElement;
